import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Columns2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhotoThumbnailStrip } from "@/components/common/photo-thumbnail-strip/photo-thumbnail-strip";
import {
  FilmstripStage,
  type FilmstripPhoto,
  type SlideZoomControls,
} from "@/components/records/record-photo-viewer/filmstrip-stage";
import { useFilmstripDrag } from "@/components/records/record-photo-viewer/use-filmstrip-drag";
import { CompareWipe } from "./compare-wipe";
import {
  comparePair,
  firstUpdateIndex,
  photoCaption,
  type IssuePhoto,
} from "./issue-photos";

export interface IssuePhotoViewerProps {
  /** Every photo on the issue, oldest first; see `issuePhotos`. */
  photos: IssuePhoto[];
  /** Index to open at. */
  initialIndex: number;
  onClose: () => void;
}

/**
 * Viewing pages through the photos; picking waits for a second photo; comparing
 * shows the wipe. The anchor of a comparison is always the filmstrip's current
 * photo, which nothing moves while comparing, so only the other photo is held
 * here.
 */
type Mode =
  | { kind: "view" }
  | { kind: "pick" }
  | { kind: "compare"; other: number };

const roundButton =
  "flex h-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20";

/**
 * Every photo on an issue, full-screen: the record's evidence then each update's
 * photos, oldest first. Swipe, chevrons, arrow keys or the Before and Updates
 * thumbnail rows move between them, with the record viewer's zoom on each.
 * **Compare** lays any two over each other with a before/after wipe, the older
 * on the left.
 * See [[issue-photo-viewer.spec]].
 */
export function IssuePhotoViewer({
  photos,
  initialIndex,
  onClose,
}: IssuePhotoViewerProps) {
  const count = photos.length;
  const multi = count > 1;
  const clampedInitial = Math.min(
    Math.max(initialIndex, 0),
    Math.max(count - 1, 0),
  );

  const [mode, setMode] = useState<Mode>({ kind: "view" });
  const [zoomed, setZoomed] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);

  const film = useFilmstripDrag({
    count,
    zoomed,
    initialIndex: clampedInitial,
    stageRef,
  });
  const { index, step, goTo } = film;

  // A committed move always lands on a fresh slide at rest scale.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setZoomed(false);
  }, [index]);

  const controlsRef = useRef<SlideZoomControls | null>(null);
  const registerControls = useCallback(
    (controls: SlideZoomControls | null) => {
      controlsRef.current = controls;
    },
    [],
  );

  // The shared stage's slide shape; issue photos have no offline asset key.
  const slides = useMemo<FilmstripPhoto[]>(
    () =>
      photos.map((photo) => ({ id: photo.id, assetKey: "", src: photo.src })),
    [photos],
  );

  // Escape steps back one level: out of picking or comparing first, then out
  // of the viewer. Capture phase plus stopPropagation so neither the issue's
  // side panel nor any dialog beneath also takes it. Arrows page only while
  // the filmstrip is on stage; in the wipe the divider's handle owns them.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (mode.kind === "view") onClose();
        else setMode({ kind: "view" });
      } else if (mode.kind !== "compare") {
        if (e.key === "ArrowRight") step(1);
        else if (e.key === "ArrowLeft") step(-1);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [mode.kind, onClose, step]);

  const selectThumbnail = (target: number) => {
    if (mode.kind === "view") {
      if (target === index) return;
      // A thumbnail is an explicit choice, so it moves even while zoomed; reset
      // first so a neighbour that stays mounted is not left magnified.
      controlsRef.current?.reset();
      goTo(target);
      return;
    }
    // Picking or comparing: the photo on stage is the anchor, and a photo is
    // never compared with itself.
    if (target === index) return;
    // The filmstrip leaves the stage while comparing and comes back at rest
    // scale, so the zoom flag must not outlive it.
    setZoomed(false);
    setMode({ kind: "compare", other: target });
  };

  const toggleCompare = () => {
    if (mode.kind === "view") {
      setMode({ kind: "pick" });
    } else {
      // Cancel (picking) or Done (comparing): back to the anchor, unmoved.
      setMode({ kind: "view" });
    }
  };

  const pair = mode.kind === "compare" ? comparePair(index, mode.other) : null;
  const highlighted = pair ? [pair.before, pair.after] : [index];
  const current = photos[index];

  // The thumbnails split where the update photos begin: the record's photos in
  // a Before row over every update's in an Updates row, the same split as the
  // page's Before and Latest update tiles. Still one list, so each tile keeps
  // its whole-list position and swiping runs straight across the join. A row
  // with nothing in it is left out. See [[issue-photo-viewer.spec]].
  const split = firstUpdateIndex(photos);
  const rows = [
    {
      key: "before",
      title: "Before",
      label: "Before photos",
      start: 0,
      end: split,
    },
    {
      key: "updates",
      title: "Updates",
      label: "Update photos",
      start: split,
      end: count,
    },
  ].filter((row) => row.end > row.start);

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-black"
      style={{ touchAction: "none" }}
      role="dialog"
      aria-label="Issue photos"
      aria-modal="true"
    >
      {/* Top bar. A three-column grid so the title stays centred whatever the
          width of the Compare pill on the right. */}
      <div
        className="z-10 grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-2.5 p-3"
        style={{
          paddingTop: "max(env(safe-area-inset-top, 0px), 0.75rem)",
          paddingLeft: "max(env(safe-area-inset-left, 0px), 0.75rem)",
          paddingRight: "max(env(safe-area-inset-right, 0px), 0.75rem)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className={cn(roundButton, "w-10 justify-self-start")}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="min-w-0 text-center text-white" aria-live="polite">
          <p className="text-[13px] font-medium text-white/90">
            {mode.kind === "compare" ? "Comparing" : `${index + 1} of ${count}`}
          </p>
          {mode.kind !== "compare" && current && (
            <p className="truncate text-xs text-white/60">
              {photoCaption(current)}
            </p>
          )}
        </div>

        {multi ? (
          mode.kind === "view" ? (
            // Keyed apart from the exit button so the label swap is instant
            // rather than a colour fade from mint through to grey.
            <button
              key="compare"
              type="button"
              onClick={toggleCompare}
              className="flex h-10 items-center gap-1.5 justify-self-end rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <Columns2 className="h-4 w-4" aria-hidden="true" />
              Compare
            </button>
          ) : (
            <button
              key="exit"
              type="button"
              onClick={toggleCompare}
              className={cn(
                roundButton,
                "justify-self-end px-4 text-sm font-semibold",
              )}
            >
              {mode.kind === "pick" ? "Cancel" : "Done"}
            </button>
          )
        ) : (
          <span aria-hidden="true" />
        )}
      </div>

      {pair ? (
        <div className="relative min-h-0 flex-1">
          <CompareWipe
            before={photos[pair.before]}
            after={photos[pair.after]}
          />
        </div>
      ) : (
        <FilmstripStage
          photos={slides}
          film={film}
          stageRef={stageRef}
          zoomed={zoomed}
          onZoomChange={setZoomed}
          registerControls={registerControls}
        >
          {mode.kind === "pick" && (
            <div className="pointer-events-none absolute inset-x-0 bottom-3 z-[9] flex justify-center">
              <span
                role="status"
                className="rounded-full bg-black/70 px-3.5 py-1.5 text-[13px] font-medium text-white"
              >
                Tap a photo to compare
              </span>
            </div>
          )}
        </FilmstripStage>
      )}

      {multi ? (
        rows.map((row, i) => (
          <PhotoThumbnailStrip
            key={row.key}
            photos={photos.slice(row.start, row.end)}
            offset={row.start}
            total={count}
            currentIndex={index}
            highlighted={highlighted}
            title={row.title}
            label={row.label}
            compact
            safeAreaBottom={i === rows.length - 1}
            onSelect={selectThumbnail}
          />
        ))
      ) : (
        <div
          className="shrink-0"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        />
      )}
    </div>
  );
}
